import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

/** PATCH /admin/claims/:id — mirrors the section submits of UpdateClaimModal.vue. */
export function useUpdateClaim(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      successMessage,
    }: {
      payload: Parameters<typeof adminAPI.updateClaim>[1];
      successMessage: string;
    }) =>
      notify.promise(adminAPI.updateClaim(claimId, payload), {
        loading: "Mise à jour du dossier…",
        success: successMessage,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Families of the claim — mirrors UpdateClaimModal.claimFamilies (Vue). */
export function useClaimFamilies(claimId: string, enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "families"],
    queryFn: () => adminAPI.listClaimFamilies(claimId),
    enabled,
  });
}

/** Mirrors UpdateClaimModal.syncFamilyChanges (Vue) — replaces the claim family structure. */
export function useUpdateClaimFamilies(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (families: Array<{ familyId: string; usersClaimsIds: string[] }>) =>
      notify.promise(adminAPI.updateClaimFamilies(claimId, { families }), {
        loading: "Modification des familles…",
        success: "Structure familiale(s) modifiée(s) avec succès",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors UpdateClaimModal.extractInNewFamily (Vue). */
export function useExtractInNewFamily(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userClaimId: string) =>
      notify.promise(adminAPI.extractInNewFamily(claimId, userClaimId), {
        loading: "Création de la famille…",
        success: "Nouvelle famille créée avec succès",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Segments of the reservation — mirrors UpdateClaimModal.segments (Vue). */
export function useReservationItinerary(reservationId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.claims.all, "reservation-itinerary", reservationId],
    queryFn: () => adminAPI.retrieveReservationItinerary(reservationId as string),
    enabled: Boolean(reservationId),
  });
}

/** Active tenants — mirrors UpdateClaimModal.tenants (Vue, Upclaim only). */
export function useTenants(enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.claims.all, "tenants"],
    queryFn: () => adminAPI.listTenants(),
    enabled,
  });
}

/** Mirrors UpdateClaimModal.changeTenant (Vue), plus the office ID (TECH-1409). */
export function useChangeClaimTenant(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { tenantId: string; officeId?: number }) =>
      notify.promise(adminAPI.changeClaimTenant(claimId, payload), {
        loading: "Changement de tenant…",
        success: "Tenant et office ID modifiés avec succès",
        error: "Erreur lors du changement de tenant",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors UpdateClaimModal.submitSplitClaim (Vue, TECH-11). */
export function useSplitClaim(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userClaimIds: string[]) =>
      notify.promise(adminAPI.splitClaim(claimId, { userClaimIds }), {
        loading: "Scission du dossier…",
        success: ({ newClaimNumber }) => `Dossier scindé — nouveau dossier n°${newClaimNumber}`,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/**
 * Corrects the carrier that actually operated a flight (TECH-27).
 *
 * Deliberately not part of `updateClaim`: a `Flight` row is shared by every claim on that flight,
 * so this changes all of them. The claim's adversary — who we sue — is a separate, per-claim field
 * and is never touched here.
 */
export function useUpdateFlightOperatingAirline(claimId: string, flightId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (operatingAirlineId: string) =>
      notify.promise(adminAPI.updateFlightOperatingAirline(flightId, { operatingAirlineId }), {
        loading: "Mise à jour de la compagnie opératrice…",
        success: ({ affectedClaimsCount }) =>
          `Compagnie opératrice mise à jour sur ${affectedClaimsCount} dossier(s)`,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** How many claims a correction on this flight would affect — shown before saving. */
export function useFlightClaimsCount(flightId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["flights", flightId, "claims-count"],
    queryFn: async () => (await adminAPI.getFlightClaimsCount(flightId)).count,
    enabled: enabled && Boolean(flightId),
  });
}
